import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useUserData } from '../../../hooks/useUserData';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';

// Only manage portfolio-related events here
const EVENT_TYPES = [
  { value: 'lump_inflow', label: 'Lump-sum Inflow' },
  { value: 'asset_sale', label: 'Asset Sale' }
];

const SpecialEventsSection = () => {
  const { userData, saveSpecialEvents, loading } = useUserData();
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;

  const startYear = useMemo(() => Number(userData?.setup?.startYear) || new Date().getFullYear(), [userData]);
  const horizon = useMemo(() => Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20)), [userData]);
  const yearOptions = useMemo(() => Array.from({ length: horizon + 1 }, (_, i) => startYear + i), [startYear, horizon]);

  const assetOptions = useMemo(() => (userData?.portfolio || []).map(a => ({ id: String(a.id), name: a.name || a.type || 'Asset' })), [userData]);
  const debtOptions = useMemo(() => (userData?.debts || []).map(d => ({ id: String(d.id), name: d.name || 'Loan' })), [userData]);

  const initialRows = useMemo(() => {
    const persisted = Array.isArray(userData?.specialEvents) ? userData.specialEvents : [];
    // Only show events relevant to portfolio section
    return persisted.filter(e => ['lump_inflow', 'asset_sale'].includes(e.type)).map(e => ({
      id: e.id || `${Date.now()}-${Math.random()}`,
      type: e.type || 'lump_inflow',
      year: Number(e.year) || startYear,
      amount: Math.max(0, Number(e.amount) || 0),
      targetId: e.targetId || '',
      description: e.description || '',
      allocations: Array.isArray(e.allocations) && e.allocations.length
        ? e.allocations.map(a => ({ destination: a.destination, percent: Number(a.percent) || 0 }))
        : [{ destination: 'Cash', percent: 100 }]
    }));
  }, [userData, startYear]);

  const [rows, setRows] = useState(initialRows);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const addRow = () => {
    setRows(prev => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, type: 'lump_inflow', year: startYear, amount: 0, targetId: '', description: '', allocations: [{ destination: 'Cash', percent: 100 }] }
    ]);
  };

  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));
  const updateRow = (id, patch) => setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));

  const requiresTarget = (type) => type === 'asset_sale';
  const allocationDestinations = ['Equity','Mutual Funds','Real Estate','PPF','Fixed Deposits','Gold','Emergency Fund','Govt Fund','Cash'];

  const validateRow = (r) => {
    const typeOk = EVENT_TYPES.some(t => t.value === r.type);
    const yearOk = yearOptions.includes(Number(r.year));
    const amountOk = Number(r.amount) > 0;
    const targetOk = requiresTarget(r.type) ? Boolean(r.targetId) : true;
    return typeOk && yearOk && amountOk && targetOk;
  };

  // Allow saving when there are zero rows to persist deletion of all inflow events
  const isValid = useMemo(() => rows.length === 0 || rows.every(validateRow), [rows]);

  const saveEvents = async () => {
    setIsSaving(true);
    setSaveMessage('');
    setSaveError('');
    try {
      const payload = rows.map(r => ({
        type: r.type,
        year: Number(r.year),
        amount: Number(r.amount),
        targetId: r.targetId || null,
        description: r.description || '',
        allocations: (r.allocations && r.allocations.length
          ? r.allocations
          : [{ destination: 'Cash', percent: 100 }]).map(a => ({
            destination: a.destination,
            percent: Number(a.percent) || 0
          }))
      }));
      // Merge with other special events managed outside portfolio
      const existing = Array.isArray(userData?.specialEvents) ? userData.specialEvents : [];
      const others = existing.filter(e => !['lump_inflow', 'asset_sale'].includes(e.type));
      await saveSpecialEvents([...others, ...payload]);
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(''), 2500);
    } catch (e) {
      setSaveError('Failed to save');
      setTimeout(() => setSaveError(''), 4000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CalendarPlus" size={16} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Special Inflow Events</h3>
            <p className="text-sm text-muted-foreground">Events run before surplus/deficit logic each year.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={addRow} disabled={!effectiveCanWrite} title={isDemoModeActive ? 'Cannot add in demo mode' : 'Add Event'}>
            <Icon name="Plus" size={16} />
            Add Event
          </Button>
          <Button onClick={saveEvents} disabled={!isValid || loading || isSaving || !effectiveCanWrite} title={isDemoModeActive ? 'Cannot save in demo mode' : 'Save Events'}>
            <Icon name="Save" size={16} />
            {isSaving ? 'Saving…' : 'Save Events'}
          </Button>
          {saveMessage && <span className="text-xs text-success">{saveMessage}</span>}
          {saveError && <span className="text-xs text-destructive">{saveError}</span>}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full table-fixed">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-40">Event Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-24">Year</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-28">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-48">Target (if applicable)</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-[28rem]">Description</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-[22rem]">Allocate To</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground w-24">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => {
              const showAsset = r.type === 'asset_sale';
              const showDebt = false;
              return (
                <tr key={r.id} className="hover:bg-muted/30 transition-colors duration-200">
                  <td className="px-4 py-3">
                    <select
                      className="text-sm border border-border rounded px-2 py-1 bg-background"
                      value={r.type}
                      onChange={(e) => updateRow(r.id, { type: e.target.value, targetId: '' })}
                    >
                      {EVENT_TYPES.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      className="text-sm border border-border rounded px-2 py-1 bg-background"
                      value={r.year}
                      onChange={(e) => updateRow(r.id, { year: Number(e.target.value) })}
                    >
                      {yearOptions.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min={0}
                      step={1000}
                      className="w-28 text-sm border rounded px-2 py-1 bg-background"
                      value={r.amount}
                      onChange={(e) => updateRow(r.id, { amount: Math.max(0, Number(e.target.value) || 0) })}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {showAsset && (
                      <select
                        className="text-sm border border-border rounded px-2 py-1 bg-background w-44 truncate"
                        value={r.targetId}
                        onChange={(e) => updateRow(r.id, { targetId: e.target.value })}
                      >
                        <option value="">Select asset…</option>
                        {assetOptions.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    )}
                    {showDebt && (
                      <select
                        className="text-sm border border-border rounded px-2 py-1 bg-background"
                        value={r.targetId}
                        onChange={(e) => updateRow(r.id, { targetId: e.target.value })}
                      >
                        <option value="">Select loan…</option>
                        {debtOptions.map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    )}
                    {!showAsset && !showDebt && (
                      <span className="text-xs text-muted-foreground">Not required</span>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-[24rem]">
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                      value={r.description}
                      maxLength={50}
                      onChange={(e) => updateRow(r.id, { description: e.target.value })}
                      placeholder="Describe the event (max 50 chars)"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-2">
                      {(r.allocations || [{ destination: 'Cash', percent: 100 }]).map((al, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <select
                            className="text-sm border border-border rounded px-2 py-1 bg-background"
                            value={al.destination}
                            onChange={(e) => {
                              const next = [...(r.allocations || [{ destination: 'Cash', percent: 100 }])];
                              next[idx] = { ...next[idx], destination: e.target.value };
                              updateRow(r.id, { allocations: next });
                            }}
                          >
                            {allocationDestinations.map(dest => (
                              <option key={dest} value={dest}>{dest}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            className="w-20 text-sm border border-border rounded px-2 py-1 bg-background"
                            value={al.percent}
                            min={0}
                            max={100}
                            onChange={(e) => {
                              const v = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                              const next = [...(r.allocations || [{ destination: 'Cash', percent: 100 }])];
                              next[idx] = { ...next[idx], percent: v };
                              updateRow(r.id, { allocations: next });
                            }}
                          />
                          <span className="text-sm text-muted-foreground">%</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={() => {
                              const next = [...(r.allocations || [])];
                              next.splice(idx, 1);
                              updateRow(r.id, { allocations: next.length ? next : [{ destination: 'Cash', percent: 100 }] });
                            }}
                            title="Remove allocation"
                          >
                            <Icon name="Trash2" size={14} />
                          </Button>
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const next = [...(r.allocations || [{ destination: 'Cash', percent: 100 }]), { destination: 'Cash', percent: 0 }];
                            updateRow(r.id, { allocations: next });
                          }}
                        >
                          <Icon name="Plus" size={14} /> Add Allocation
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          Total: {(r.allocations || [{ percent: 100 }]).reduce((s,a)=>s+(Number(a.percent)||0),0)}%
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(r.id)}
                      className="text-muted-foreground hover:text-destructive"
                      title="Remove event"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={7}>No events added yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SpecialEventsSection;


